import React, { useState, useEffect } from 'react';
import { Mail, Shield, UserPlus } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { tabService, type WorkspaceRole } from '@/services/tabs';
import { toast } from 'sonner';

interface InviteMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  workspaceId: string;
  workspaceName: string;
  onInvited?: () => void;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const ROLES: { value: WorkspaceRole; label: string; description: string }[] = [
  { value: 'viewer', label: 'Viewer', description: 'Read-only access to the shared tabs.' },
  { value: 'editor', label: 'Editor', description: 'Can share and remove tabs.' },
  { value: 'admin', label: 'Admin', description: 'Can invite, remove, and change member roles.' },
];

export const InviteMemberModal: React.FC<InviteMemberModalProps> = ({
  isOpen,
  onClose,
  workspaceId,
  workspaceName,
  onInvited,
}) => {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<WorkspaceRole>('viewer');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      // Reset on close so a stale email/role never leaks to the next open.
      setEmail('');
      setRole('viewer');
      setError(null);
      setSubmitting(false);
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const trimmed = email.trim().toLowerCase();
    if (!EMAIL_REGEX.test(trimmed)) {
      setError('Enter a valid email address.');
      return;
    }
    setSubmitting(true);
    try {
      await tabService.inviteToWorkspace(workspaceId, trimmed, role);
      toast.success(`Invited ${trimmed} as ${role}`);
      onInvited?.();
      onClose();
    } catch (err) {
      const msg = (err as Error)?.message ?? 'Failed to send invitation';
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Invite to ${workspaceName}`} size="sm">
      <form onSubmit={handleSubmit} className="space-y-4">
        <p className="text-sm text-khiimori-text-muted">
          An email with an accept link will be sent to the invitee. Invitations expire after 7 days.
        </p>

        <label className="block">
          <span className="text-sm font-medium text-khiimori-text-primary flex items-center gap-2 mb-1">
            <Mail className="w-4 h-4" /> Email
          </span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="collaborator@example.com"
            autoFocus
            required
            className="w-full px-3 py-2 text-sm rounded-md border border-khiimori-border bg-khiimori-bg-elevated/50 text-khiimori-text-primary focus:outline-none focus:ring-2 focus:ring-primary-500/40"
          />
        </label>

        <div>
          <span className="text-sm font-medium text-khiimori-text-primary flex items-center gap-2 mb-2">
            <Shield className="w-4 h-4" /> Role
          </span>
          <div className="space-y-2">
            {ROLES.map((r) => (
              <label
                key={r.value}
                className={`flex items-start gap-2 p-2 rounded-md border cursor-pointer transition-colors ${
                  role === r.value
                    ? 'border-primary-500/50 bg-primary-500/10'
                    : 'border-khiimori-border hover:bg-khiimori-bg-elevated/40'
                }`}
              >
                <input
                  type="radio"
                  name="invite-role"
                  value={r.value}
                  checked={role === r.value}
                  onChange={() => setRole(r.value)}
                  className="mt-1"
                />
                <span className="flex-1">
                  <span className="text-sm font-medium text-khiimori-text-primary">{r.label}</span>
                  <span className="block text-xs text-khiimori-text-muted">{r.description}</span>
                </span>
              </label>
            ))}
          </div>
        </div>

        {error && (
          <p className="text-sm text-red-500" role="alert">
            {error}
          </p>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" disabled={submitting} className="gap-1.5">
            <UserPlus className="w-4 h-4" />
            {submitting ? 'Sending…' : 'Send invite'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default InviteMemberModal;
